import { useState, useEffect, useMemo, useRef } from "react";
import * as XLSX from 'xlsx';
import { Grid, Typography, Alert } from "@mui/material";
import { StyledButton, StyledDataGrid } from "./styles";
import {
  GridActionsCellItem
} from "@mui/x-data-grid";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";

//Components
import RouterBreadrumbs from "../../components/Breadcrumbs/RouterBreadcrumbs";
import Title from "../../components/Typography/Title";
import InformationDialog from "../../components/Dialog/InformationDialog";
import ConfirmationDialog from "../../components/Dialog/ConfirmationDialog";
import IconsWithTooltip from "../../components/Icons/IconsWithTooltip";
import CustomToolbar from "../../components/Toolbar/CustomToolbar";
import NonSystemUserEditDialog from "../../components/Dialog/NonSystemUserEditDialog";

//custom hook
import { useFetch } from "../../hooks/useFetch";
import { useUpdate } from "../../hooks/useUpdate";

//constants
import { API_URLS } from "../../Constants";

//context
import { useAuth } from "../../context/AuthContext";

//services
import AuthHeader from "../../services/AuthHeader";
import AuthToken from "../../services/AuthToken";
import { getFormattedDate } from "../../utils/getFormattedDateTime";

const pathItems = ["Dashboard", "Non-System Users"];

//This page is to manage  users for email reminders who don't have access to the system. This page will only be accessible to Super Admin users.
export default function NonSystemUsers() {
  const [usersData, setUsersData] = useState([]);
  const usersAPI = API_URLS["API_NON_SYSTEM_USERS"];
  const { auth } = useAuth();
  const [reloadFlag, setReloadFlag] = useState(false);
  const [activeFilter, setActiveFilter] = useState({ column: '', value: '' });

  //custom hooks
  const fetcher = useFetch("get", usersAPI);
  const postFetcher = useUpdate("post", usersAPI);
  const putFetcher = useUpdate("put", usersAPI);
  const deleteFetcher = useUpdate("delete", usersAPI);

  //Fetch data from API when the component renders.
  useEffect(() => {
    fetcher.setRequest({});
  }, [reloadFlag]);

  //Once response is received, set appropriate state variables.
  useEffect(() => {
    const response = fetcher.serverResponse;
    if (response?.success && response.success) {
      let allUsersData = response.data;

      // Sort the users alphabetically by full name
      const sortedUsersData = Array.isArray(allUsersData)
        ? allUsersData.toSorted((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        )
        : null;
      setUsersData(sortedUsersData || []);
    }
  }, [fetcher.serverResponse]);

  //Once data from POST request is fetched, refetch the data
  useEffect(() => {
    if (postFetcher?.serverResponse?.success) {
      setReloadFlag((prev) => !prev);
    }
    else if (postFetcher?.serverResponse?.error) {
      console.error(
        "Error in POST request: ",
        postFetcher.serverResponse.error
      );
      setInformationDialog({
        open: true,
        title: "Error",
        message: postFetcher.serverResponse.error,
      });
    }
  }, [postFetcher.serverResponse]);

  //Once data from PUT request is fetched, refetch the data
  useEffect(() => {
    if (
      putFetcher?.serverResponse?.success &&
      putFetcher.serverResponse.success
    ) {
      setReloadFlag((prev) => !prev);
    } else if (putFetcher?.serverResponse?.error)
      console.error("Error in PUT request: ", putFetcher.serverResponse.error);
  }, [putFetcher.serverResponse]);

  //Filter the rows based on the active filter value and column.
  const filteredRows = useMemo(() => {
    if (!usersData) return [];
    if (!activeFilter.value || !activeFilter.column) {
      return usersData;
    }
    const searchTerm = activeFilter.value.toLowerCase();
    const column = activeFilter.column;

    return usersData.filter(row => {
      if (row[column] == null) return false;
      return String(row[column]).toLowerCase().includes(searchTerm);
    });
  }, [usersData, activeFilter]);

  //React Hooks
  const [selectedRows, setSelectedRows] = useState([]);
  const [confirmationDialog, setConfirmationDialog] = useState({ open: false });
  const [informationDialog, setInformationDialog] = useState({ open: false });
  const [instructionsDialog, setInstructionsDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  //Opens instructions dialog before file input
  const handleUploadClick = () => {
    setInstructionsDialog(true);
  };

  //Delete multiple selected rows after confirmation
  const handleDeleteSelectedRows = () => {
    if (!selectedRows || selectedRows.length === 0) return;
    setConfirmationDialog({
      open: true,
      title: "Delete Selected Users",
      message: `Are you sure you want to delete ${selectedRows.length} selected user(s)?`,
      onConfirm: async () => {
        try {
          setIsUploading(true);
          for (const email of selectedRows) {
            await deleteFetcher.executeUpdate({ email });
          }
          setSelectedRows([]);
          setReloadFlag(prev => !prev);
        } catch (err) {
          console.error('Error deleting selected users:', err);
          setInformationDialog({ open: true, title: 'Error', message: 'Failed to delete selected users. Check console for details.' });
        } finally {
          setIsUploading(false);
          setConfirmationDialog(prev => ({ ...prev, open: false }));
        }
      }
    });
  };

  //Closes instructions dialog and opens hidden file input
  const handleInstructionsClose = () => {
    setInstructionsDialog(false);
    fileInputRef.current.value = null;
    fileInputRef.current.click();
  };

  //Parses uploaded Excel file, validates required columns, and posts each row
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];

    // Check for duplicate column names using the raw header row
    const headerRow = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] || [];
    const normalizedHeaders = headerRow.map(h => String(h).toLowerCase().trim());
    const headerCounts = normalizedHeaders.reduce((acc, h) => { acc[h] = (acc[h] || 0) + 1; return acc; }, {});
    const duplicateHeaders = Object.entries(headerCounts).filter(([, count]) => count > 1).map(([h]) => h);
    if (duplicateHeaders.length > 0) {
      setInformationDialog({ open: true, title: 'Error', message: `Duplicate column name(s) found: ${duplicateHeaders.join(', ')}. Please ensure all column names are unique.` });
      return;
    }

    const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

    if (!rows || rows.length === 0) {
      setInformationDialog({ open: true, title: 'Error', message: 'The uploaded file is empty.' });
      return;
    }

    const firstRowKeys = new Set(Object.keys(rows[0]).map(k => k.toLowerCase().trim()));
    const missingCols = ['email', 'name', 'region'].filter(col => !firstRowKeys.has(col));
    if (missingCols.length > 0) {
      setInformationDialog({ open: true, title: 'Error', message: `Missing required column(s): ${missingCols.join(', ')}` });
      return;
    }

    // Fetch valid regions for validation
    let validRegionNames = new Set();
    try {
      const isTokenValid = AuthToken(new Date());
      if (!isTokenValid) {
        setInformationDialog({ open: true, title: 'Error', message: 'Session expired. Please log in again.' });
        return;
      }
      const regionsRes = await fetch(API_URLS["API_GET_ALL_REGIONS"], { headers: AuthHeader() });
      if (regionsRes.ok) {
        const regionsData = await regionsRes.json();
        regionsData.forEach(r => validRegionNames.add(r.name.toLowerCase()));
      }
    } catch (err) {
      console.error('Failed to fetch regions for validation:', err);
    }

    const validationErrors = [];
    const validRows = [];
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    rows.forEach((row, index) => {
      const normalized = {};
      Object.entries(row).forEach(([k, v]) => { normalized[k.toLowerCase().trim()] = v; });

      const email = String(normalized['email'] || '').trim();
      const name = String(normalized['name'] || '').trim();
      const region = String(normalized['region'] || '').trim();

      if (!email) validationErrors.push(`Row ${index + 2}: email is required.`);
      else if (!emailPattern.test(email)) validationErrors.push(`Row ${index + 2}: invalid email "${email}".`);
      if (!name) validationErrors.push(`Row ${index + 2}: name is required.`);
      if (!region) validationErrors.push(`Row ${index + 2}: region is required.`);
      else if (validRegionNames.size > 0 && !validRegionNames.has(region.toLowerCase())) {
        validationErrors.push(`Row ${index + 2}: invalid region "${region}".`);
      }

      if (email && emailPattern.test(email) && name && region && (validRegionNames.size === 0 || validRegionNames.has(region.toLowerCase()))) {
        const reminder1 = String(normalized['reminder 1'] || normalized['reminder1'] || '').trim().toLowerCase();
        const reminder2 = String(normalized['reminder 2'] || normalized['reminder2'] || '').trim().toLowerCase();
        const reminder3 = String(normalized['reminder 3'] || normalized['reminder3'] || '').trim().toLowerCase();
        const active = String(normalized['active'] || '').trim().toLowerCase();
        const validReminderValues = ['to', 'cc'];
        const validActiveValues = ['yes', 'no'];

        if (reminder1 && !validReminderValues.includes(reminder1)) {
          validationErrors.push(`Row ${index + 2}: invalid value "${normalized['reminder 1']}" for reminder 1. Accepted values are "to", "cc", or blank.`);
        }
        if (reminder2 && !validReminderValues.includes(reminder2)) {
          validationErrors.push(`Row ${index + 2}: invalid value "${normalized['reminder 2']}" for Email 2. Accepted values are "to", "cc", or blank.`);
        }
        if (reminder3 && !validReminderValues.includes(reminder3)) {
          validationErrors.push(`Row ${index + 2}: invalid value "${normalized['reminder 3']}" for Email 3. Accepted values are "to", "cc", or blank.`);
        }
        if (active && !validActiveValues.includes(active)) {
          validationErrors.push(`Row ${index + 2}: invalid value "${normalized['active']}" for Active. Accepted values are "yes", "no", or blank.`);
        }

        const remindersValid = (!reminder1 || validReminderValues.includes(reminder1))
          && (!reminder2 || validReminderValues.includes(reminder2))
          && (!reminder3 || validReminderValues.includes(reminder3));
        const activeValid = !active || validActiveValues.includes(active);

        if (remindersValid && activeValid) {
          validRows.push({
            name: name,
            email: email.toLowerCase(),
            active: active ? active === 'yes' : true,
            reminder1: reminder1 || null,
            reminder2: reminder2 || null,
            reminder3: reminder3 || null,
            role: String(normalized['role'] || '').trim() || null,
            region: region,
            agency: String(normalized['agency'] || '').trim() || null
          });
        }
      }
    });

    if (validationErrors.length > 0) {
      setInformationDialog({ open: true, title: 'Validation Errors', message: validationErrors.join('\n') });
      return;
    }
    setIsUploading(true);
    for (const rowData of validRows)
      await postFetcher.executeUpdate(rowData);
    setIsUploading(false);
  };

  //Data Grid columns
  const columns = [
    {
      field: "email", headerName: "Email", flex: 2.5, renderCell: (params) => (
        <Typography variant="body2" key={params.id}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "name",
      headerName: "Name",
      flex: 2,
      renderCell: (params) => (
        <Typography variant="body2" key={params.id}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "region", headerName: "Region", flex: 1.5, renderCell: (params) => (
        <Typography variant="body2" key={params.id}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "agency", headerName: "Agency", flex: 1, renderCell: (params) => (
        <Typography variant="body2" key={params.id}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "role", headerName: "Role", flex: 2, renderCell: (params) => (
        <Typography variant="body2" key={params.id}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "active", headerName: "Active", flex: 1,
      valueGetter: (params) => {
        return params.row ? "Yes" : "No";
      },
      renderCell: (params) => (
        <Typography variant="body2" key={params.id}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "reminder1", headerName: "Reminder 1", flex: 1, renderCell: (params) => (
        <Typography variant="body2" key={params.id}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "reminder2", headerName: "Reminder 2", flex: 1, renderCell: (params) => (
        <Typography variant="body2" key={params.id}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "reminder3", headerName: "Reminder 3", flex: 1, renderCell: (params) => (
        <Typography variant="body2" key={params.id}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      filterable: false,
      export: false,
      getActions: (params) => {
        const actions = [];

        actions.push(
          <GridActionsCellItem
            icon={
              <IconsWithTooltip title={`Edit user ${params.row.name}`} icon={<EditIcon />} />
            }
            label={`Edit user ${params.row.name}`}
            onClick={() => handleEditSelected(params.row.email)}
          />
        );
        actions.push(
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label={`Delete user ${params.row.name}`}
            onClick={() => handleDeleteSelected(params.row.email)}
          />
        );
        return actions;
      },
    },
  ];

  //Invoked when user clicks "Edit" button in Edit Dialog.
  const handleEditConfirm = (
    editedEmail,
    editedName,
    editedRole,
    editedRegion,
    editedAgency,
    editedActive,
    editedEmail1,
    editedEmail2,
    editedEmail3,
    callback = () => { }
  ) => {

    //Update row with new data.
    const updatedRow = {
      email: editedEmail.toLowerCase(),
      name: editedName,
      role: editedRole,
      region: editedRegion,
      agency: editedAgency,
      active: editedActive.toLowerCase() === "yes" ? true : false,
      reminder1: editedEmail1,
      reminder2: editedEmail2,
      reminder3: editedEmail3
    };

    // Update data in the database.
    putFetcher.executeUpdate(updatedRow);

    //Close EditDialog.
    setEditDialog((prevState) => ({ ...prevState, open: false }));

    callback();
  };

  //User Edit: Invoked to show Edit Dialog when user clicks edit icon for the selected row.
  const handleEditSelected = (selectedRowEmail) => {
    const selectedRow = usersData.find((row) => row.email === selectedRowEmail);

    // If a row is selected then open Edit Dialog.
    if (selectedRow) {
      setSelectedRows([selectedRow]);
      setEditDialog({
        open: true,
        email: selectedRow.email,
        name: selectedRow.name,
        role: selectedRow.role,
        region: selectedRow.region,
        agency: selectedRow.agency,
        active: selectedRow.isActive ? "Yes" : "No",
        email1: selectedRow.reminder1 || null,
        email2: selectedRow.reminder2 || null,
        email3: selectedRow.reminder3 || null
      });
    }
  };

  //User Deletion: Set ConfirmationDialog properties for selected user(s).
  const handleDeleteSelected = (selectedEmail) => {
    if (selectedEmail) {
      setConfirmationDialog({
        open: true,
        title: "Delete Confirmation",
        message: `Are you sure you want to delete the selected user ?`,
        onConfirm: async () => {
          // Update the database.
          await deleteFetcher.executeUpdate({ email: selectedEmail });
          if (deleteFetcher?.serverResponse?.error)
            console.error(
              "Error in handleDeleteSelected for email " +
              selectedEmail +
              " : " +
              deleteFetcher.serverResponse.error
            );
          setReloadFlag((prev) => !prev);
          setConfirmationDialog((prevState) => ({ ...prevState, open: false }));
        },
      });
    }
  };

  return (
    <>

      {/* Breadcrumbs */}
      <Grid>
        <RouterBreadrumbs pathItems={pathItems} />
      </Grid>

      {/* Page Title */}
      <Grid item xs={12}>
        <Title title={"Non-System Users"} />
      </Grid>

      {/* Add Users via Excel upload */}
      <Grid item xs={12}>
        <input
          type="file"
          ref={fileInputRef}
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <StyledButton
          startIcon={<UploadFileIcon />}
          variant="outlined"
          onClick={handleUploadClick}
          aria-label="Add Users via Excel upload"
        >
          Add Users
        </StyledButton>

        {selectedRows?.length > 1 && (
          <StyledButton
            startIcon={<DeleteIcon />}
            variant="outlined"
            onClick={() => handleDeleteSelectedRows()}
            aria-label="Delete Selected Users"
          >
            Delete Selected
          </StyledButton>
        )}
      </Grid>

      {/* Reminder column definitions */}
      <Grid item xs={12} sx={{ paddingRight: 3, paddingBottom: 1 }}>
        <Alert severity="warning">
          <div><strong>Reminder 1:</strong> Email reminder sent 2 weeks before the quarterly deadline to notify districts to review and update their mitigation plans.</div>
          <div><strong>Reminder 2:</strong> Email reminder sent after the quarterly deadline has passed to follow up with districts that still have projects pending review.</div>
          <div><strong>Reminder 3:</strong> Annual internal email reminder to validate and confirm the list of recipients.</div>
          <div>Email reminders are sent only to <strong>active users</strong>.</div>
        </Alert>
      </Grid>

      {/* Display Data Table */}
      <Grid container sx={{ paddingRight: 3 }}>
        <StyledDataGrid
          aria-label="Non-System Users table. Use arrow keys to navigate the table."
          rows={filteredRows || []}
          columns={columns}
          getRowId={(row) => row.email}
          disableColumnMenu
          loading={fetcher.isLoading || isUploading}
          autoHeight
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 15, 20, 25]}
          slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              totalCount: usersData?.length || 0,
              filter: {
                rowsCount: (filteredRows ?? []).length,
                columns: columns,
                active: activeFilter,
                onChange: (val) => setActiveFilter(val)
              },
              exportedFilename: "Non_System_Users"
            },
            pagination: {
              labelRowsPerPage: 'Rows per page:',
              SelectProps: {
                native: true,
                inputProps: {
                  'aria-label': 'Rows per page',
                  'id': 'pagination-select-input'
                }
              }
            },
            baseCheckbox: {
              inputProps: { 'aria-label': 'Select row' }
            }
          }}
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setSelectedRows(Array.isArray(newRowSelectionModel) ? newRowSelectionModel : [newRowSelectionModel]);
          }}
          selectedRows={selectedRows}
        />
      </Grid>

      {/* Upload Instructions Dialog */}
      <ConfirmationDialog
        open={instructionsDialog}
        onClose={() => setInstructionsDialog(false)}
        onConfirm={handleInstructionsClose}
        confirmLabel="Upload File"
        cancelLabel="Cancel"
        title="Upload Instructions"
        message={
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li><strong>File Format:</strong> .xlsx or .xls only</li>
            <li><strong>Mandatory columns:</strong> Email (must be unique), Name, Region</li>
            <li>Optional columns: Role, Agency, Reminder 1, Reminder 2, Reminder 3</li>
            <li>Ensure that the columns in the file match the displayed columns in the table.</li>
            <li>
              For each reminder column, set the value to <strong>"to"</strong> if the user should receive the email directly, or <strong>"cc"</strong> if they should be copied on it. Leave blank to exclude the user from that email reminder.
              <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                <li><strong>Reminder 1:</strong> Email reminder sent 2 weeks before the quarterly deadline to notify districts to review and update their mitigation plans.</li>
                <li><strong>Reminder 2:</strong> Email reminder sent after the quarterly deadline has passed to follow up with districts that still have projects pending review.</li>
                <li><strong>Reminder 3:</strong> Annual internal email reminder to validate and confirm the list of recipients.</li>
              </ul>
            </li>
            <li>All uploaded users will be set as active by default.</li>
            <li>Email must be unique — duplicate emails will not be added.</li>
          </ul>}
      />

      {/* Information Dialog */}
      <InformationDialog
        open={informationDialog.open}
        onClose={() => setInformationDialog(prev => ({ ...prev, open: false }))}
        title={informationDialog.title}
        message={informationDialog.message}
      />

      {/* ConfirmationDialog component */}
      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() =>
          setConfirmationDialog((prevState) => ({ ...prevState, open: false }))
        }
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        onConfirm={confirmationDialog.onConfirm}
      />

      {/* NonSystemUserEditDialog component */}
      {/* NonSystemUserEditDialog component */}
      <NonSystemUserEditDialog
        open={editDialog.open}
        onClose={() => setEditDialog((prevState) => ({ ...prevState, open: false }))}
        onConfirm={handleEditConfirm}
        email={editDialog.email}
        name={editDialog.name}
        role={editDialog.role}
        region={editDialog.region}
        agency={editDialog.agency}
        active={editDialog.active}
        email1={editDialog.email1}
        email2={editDialog.email2}
        email3={editDialog.email3}
        loggedInUser={auth.user}
      />
    </>
  );
}
